import { validTimelineReplacement, NORMALIZED_TIMELINE_MAX_BYTES } from './session-observation.js';
import { validSessionCapabilities } from './normalized-session-capabilities.js';
import { hash } from 'fast-sha256';
import { NORMALIZED_EVENT_MAX_BYTES, NORMALIZED_FRAGMENT_BYTES, NORMALIZED_FRAME_MAX_BYTES, } from './normalized-transport-types.js';
const encoder = new TextEncoder();
export const utf8Bytes = (value) => encoder.encode(value);
export const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const boundedString = (value, limit) => typeof value === 'string' && value.length > 0 && utf8Bytes(value).length <= limit;
export function validNormalizedRoute(value) {
    if (!isRecord(value))
        return false;
    return boundedString(value.channel_id, 256) && (boundedString(value.task_id, 256) || value.task_id === undefined && (value.session_control_binding_id !== undefined || value.shape === 'normalized_timeline_replacement' || value.logical_shape === 'normalized_timeline_replacement'))
        && boundedString(value.protocol_version, 32)
        && (value.timeline_epoch === undefined || boundedString(value.timeline_epoch, 512))
        && (value.logical_shape === undefined || value.logical_shape === 'normalized' || value.logical_shape === 'normalized_timeline_replacement')
        && (value.session_capabilities === undefined || validSessionCapabilities(value.session_capabilities))
        && (value.session_control_binding_id === undefined || typeof value.session_control_binding_id === 'string' && /^[A-Za-z0-9_:.@/-]{1,512}$/.test(value.session_control_binding_id))
        && (value.session_control_default === undefined || typeof value.session_control_default === 'boolean')
        && Number.isSafeInteger(value.sequence) && value.sequence > 0
        && (value.agent_id === undefined || boundedString(value.agent_id, 512))
        && (value.occurred_at === undefined || (Number.isSafeInteger(value.occurred_at)
            && value.occurred_at >= 0 && value.occurred_at <= 8640000000000000));
}
export function normalizedRoute(value) {
    return {
        channel_id: value.channel_id, task_id: value.task_id, protocol_version: value.protocol_version,
        sequence: value.sequence,
        ...(value.session_control_default === undefined ? {} : { session_control_default: value.session_control_default }),
        ...(value.timeline_epoch === undefined ? {} : { timeline_epoch: value.timeline_epoch }),
        ...(value.logical_shape === undefined ? {} : { logical_shape: value.logical_shape }),
        ...(value.session_control_binding_id === undefined ? {} : { session_control_binding_id: value.session_control_binding_id }),
        ...(value.session_capabilities === undefined ? {} : { session_capabilities: structuredClone(value.session_capabilities) }),
        ...(value.agent_id === undefined ? {} : { agent_id: value.agent_id }),
        ...(value.occurred_at === undefined ? {} : { occurred_at: value.occurred_at }),
    };
}
export function sha256Hex(bytes) {
    return Array.from(hash(bytes), value => value.toString(16).padStart(2, '0')).join('');
}
export function encodeBase64(bytes) {
    let binary = '';
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary);
}
export function decodeBase64(value) {
    if (!/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value))
        return undefined;
    try {
        const bytes = Uint8Array.from(atob(value), char => char.charCodeAt(0));
        return encodeBase64(bytes) === value ? bytes : undefined;
    }
    catch {
        return undefined;
    }
}
export const validEventId = (value) => typeof value === 'string' && /^[\x21-\x7e]{1,128}$/.test(value);
export function parseNormalizedFragmentCapability(version, maxEventBytes) {
    return version === '1' && maxEventBytes === '131072'
        ? { version: 1, maxEventBytes: NORMALIZED_EVENT_MAX_BYTES } : undefined;
}
/** Callers validate and redact the public event before encoding its exact bytes. */
export function encodeNormalizedTransport(frame, options) {
    if (!validNormalizedRoute(frame) || frame.type !== 'agent_activity' || !['normalized', 'normalized_timeline_replacement'].includes(frame.shape))
        return { status: 'rejected', reason: 'invalid_route' };
    const replacement = frame.shape === 'normalized_timeline_replacement';
    if (frame.session_control_default && (replacement || !frame.session_control_binding_id || !isRecord(frame.event) || !['runtime_updated', 'agent_snapshot'].includes(frame.event.type)))
        return { status: 'rejected', reason: 'invalid_event' };
    if (replacement && (!validTimelineReplacement(frame.event, frame.protocol_version) || frame.task_id !== undefined || !frame.timeline_epoch))
        return { status: 'rejected', reason: 'invalid_event' };
    if (!isRecord(frame.event) || (frame.session_capabilities !== undefined && frame.event.type !== 'runtime_updated'))
        return { status: 'rejected', reason: 'invalid_event' };
    let eventJson;
    try {
        eventJson = JSON.stringify(frame.event);
        if (!isRecord(JSON.parse(eventJson)))
            return { status: 'rejected', reason: 'invalid_event' };
    }
    catch {
        return { status: 'rejected', reason: 'invalid_event' };
    }
    const bytes = utf8Bytes(eventJson);
    if (bytes.length > (replacement ? NORMALIZED_TIMELINE_MAX_BYTES : NORMALIZED_EVENT_MAX_BYTES))
        return { status: 'rejected', reason: 'event_limit' };
    const route = normalizedRoute(frame);
    const normal = { type: 'agent_activity', shape: frame.shape, ...route, event: JSON.parse(eventJson) };
    const frameByteLength = options.frameByteLength ?? (value => utf8Bytes(JSON.stringify(value)).length);
    const frameMaxBytes = options.frameMaxBytes ?? NORMALIZED_FRAME_MAX_BYTES;
    if (frameByteLength(normal) <= frameMaxBytes)
        return { status: 'encoded', frames: [normal], eventBytes: bytes.length, fragmented: false };
    if (options.capability?.version !== 1 || options.capability.maxEventBytes !== NORMALIZED_EVENT_MAX_BYTES)
        return { status: 'rejected', reason: 'fragmentation_unavailable' };
    if (!validEventId(options.eventId))
        return { status: 'rejected', reason: 'invalid_fragment' };
    const partCount = Math.ceil(bytes.length / NORMALIZED_FRAGMENT_BYTES);
    const digest = sha256Hex(bytes);
    const frames = [];
    for (let index = 0; index < partCount; index++) {
        const fragment = {
            type: 'agent_activity', shape: 'normalized_fragment', ...route, ...(replacement ? { logical_shape: frame.shape } : {}), fragment_version: 1,
            event_id: options.eventId, part_index: index, part_count: partCount, total_bytes: bytes.length,
            sha256: digest, data_base64: encodeBase64(bytes.subarray(index * NORMALIZED_FRAGMENT_BYTES, (index + 1) * NORMALIZED_FRAGMENT_BYTES)),
        };
        if (frameByteLength(fragment) > frameMaxBytes)
            return { status: 'rejected', reason: 'frame_limit' };
        frames.push(fragment);
    }
    return { status: 'encoded', frames, eventBytes: bytes.length, fragmented: true };
}
/** An observational transport failure must not manufacture a native turn outcome. */
export function normalizedTransportError(frame, failure) {
    const provider = isRecord(frame.event) && boundedString(frame.event.providerId, 128) ? frame.event.providerId : 'unknown';
    const details = [failure.eventId, failure.requestId].filter((value) => typeof value === 'string')
        .map(value => value.slice(0, 128)).join(', ');
    const { session_capabilities: _capabilities, session_control_default: _default, ...route } = normalizedRoute(frame);
    return {
        type: 'agent_activity', shape: 'normalized', ...route,
        event: { type: 'timeline', providerId: provider, item: {
                type: 'error', message: `Normalized event unavailable: ${failure.reason}.${details ? ` (${details})` : ''}`,
            }, resources: [] },
    };
}
//# sourceMappingURL=normalized-transport-codec.js.map